import React, { useCallback, useEffect, useRef, useState } from 'react';
import History from '../../pages/History/History';
import AuthPopup from '../AuthPopup/AuthPopup';
import BottomSheetMenu from '../BottomSheetMenu/BottomSheetMenu';
import ChangePasswordPopup from '../ChangePasswordPopup/ChangePasswordPopup';
import ForgotPasswordPopup from '../ForgotPasswordPopup/ForgotPasswordPopup';
import ResetPasswordPopup from '../ResetPasswordPopup/ResetPasswordPopup';
import './Map.css';

import {
  Application,
  Assets,
  Container,
  Graphics,
  Point,
  Rectangle,
  RenderTexture,
  Sprite,
  Text,
  Texture,
  Ticker,
} from 'pixi.js';

import { manifest } from '../../asset';
import authService from '../../services/auth.service';
import checkoutStateService from '../../services/checkout-state.service';
import dataService from '../../services/data.service';
import socketService, {
  UpdateCellRequest,
} from '../../services/socket.service';
import { Cell, MapData } from '../../ultis/types';
import { PixelEditor } from './PixelEditor';

// Các hằng số zoom
const MIN_SCALE = 0.2;
const MAX_SCALE = 10;
const ZOOM_FACTOR = 1.2;
const QUEUE_CHECK_INTERVAL = 100; // ms

/**
 * Map component for rendering interactive maps using PIXI.js
 * @param data - Map data containing regions (mienbac, taynguyen, mientay, hoangsa, truongsa)
 * @param width - Optional design width for the map (default: 1440)
 * @param height - Optional design height for the map (default: 1600)
 */
const Map: React.FC<{
  data: MapData[];
  uid: string | null;
  isLoggedIn: boolean;
  width?: number;
  height?: number;
  onRequireAuth?: () => boolean;
  resetTrigger?: number;
}> = ({
  data,
  uid,
  isLoggedIn,
  width = 1440,
  height = 1600,
  onRequireAuth,
  resetTrigger,
}) => {
  // ============================================================================
  // STATE & REFS
  // ============================================================================

  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [showSaveDraftPopup, setShowSaveDraftPopup] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [pixiLoaded, setPixiLoaded] = useState<boolean>(false);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [showForgotPasswordPopup, setShowForgotPasswordPopup] = useState(false);
  const [showResetPasswordPopup, setShowResetPasswordPopup] = useState(false);
  const [resetPasswordUsername, setResetPasswordUsername] = useState('');
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [showChangePasswordPopup, setShowChangePasswordPopup] = useState(false);
  const [showBottomMenu, setShowBottomMenu] = useState(false);

  // PIXI Refs
  const pixiRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const containerRef = useRef<Container>(new Container());

  // Rendering Refs
  const graphicsCellRef = useRef<Graphics[]>([]);
  const areaSpritesRef = useRef<Sprite[]>([]);
  const areaTexturesRef = useRef<RenderTexture[]>([]);

  // Real-time Update System
  const updateQueueRef = useRef<Set<number>>(new Set());
  const prevDataRef = useRef<MapData[]>([]);

  // Constants
  const cellSideOfSquare = 10;

  let dragging = false;
  let touchDragging = false; // Biến riêng cho touch drag
  let lastPos = new Point(0, 0);
  let dragStartPos = new Point(0, 0);
  let dragThreshold = 5; // Ngưỡng để xác định có phải drag hay không

  // Pinch zoom state
  const pinchState = useRef({
    initialDistance: 0,
    initialScale: 1,
    initialCenter: { x: 0, y: 0 },
  });

  const debugTextRef = useRef<Text | null>(null);
  // Track pointer IDs that clicked on toolbar
  const toolbarPointerIdsRef = useRef<Set<number>>(new Set());
  // FPS text state
  const fpsTextRef = useRef<Text>(
    new Text('FPS: 0', {
      fontSize: 16,
      fill: '#00ff00',
      fontFamily: 'Arial',
      fontWeight: 'bold',
    })
  );
  // FPS update throttle
  const lastFPSUpdateRef = useRef<number>(0);
  const FPS_UPDATE_INTERVAL = 500; // Cập nhật FPS mỗi 500ms

  // Interaction Refs
  const lastSquareClickRef = useRef<number>(null);
  const isToolbarClickRef = useRef<boolean>(false);
  const pixelEditorRef = useRef<PixelEditor | null>(null);
  const isPixelEditorOpenRef = useRef<boolean>(false);

  // Initial position and scale for reset functionality
  const initialMapStateRef = useRef<{ x: number; y: number; scale: number } | null>(null);

  // ============================================================================
  // REAL-TIME UPDATE SYSTEM
  // ============================================================================

  /**
   * Detect data changes and queue areas for re-render
   * - Compares current data with previous snapshot
   * - Only queues areas that have changed cells
   * - Batched rendering happens in game loop (every 100ms)
   */
  useEffect(() => {
    if (pixiLoaded && data.length > 0) {
      // Compare with previous data to find changed cells
      const changedAreaIndexes = new Set<number>();

      data.forEach((area, areaIndex) => {
        const prevArea = prevDataRef.current[areaIndex];
        if (!prevArea) {
          // First render or new area
          changedAreaIndexes.add(areaIndex);
          return;
        }

        // Check if any cell in this area has changed BY COMPARING CELL INDEX (i)
        const hasChanges = area.data.some((cell) => {
          // Find matching cell in previous data by cell index (i)
          const prevCell = prevArea.data.find((c) => c.i === cell.i);
          if (!prevCell) return true; // New cell

          // Compare timestamp to detect changes
          return (
            cell.timestamp !== prevCell.timestamp ||
            cell.o !== prevCell.o ||
            JSON.stringify(cell.c) !== JSON.stringify(prevCell.c)
          );
        });

        if (hasChanges) {
          changedAreaIndexes.add(areaIndex);
        }
      });

      // Add changed areas to update queue
      if (changedAreaIndexes.size > 0) {
        changedAreaIndexes.forEach((areaIndex) => {
          updateQueueRef.current.add(areaIndex);
        });

        // Store the NEW data for rendering later
        prevDataRef.current = JSON.parse(JSON.stringify(data));
      } else {
        // No changes, keep previous
        if (prevDataRef.current.length === 0) {
          prevDataRef.current = JSON.parse(JSON.stringify(data));
        }
      }
    }
  }, [data, pixiLoaded]);

  // ============================================================================
  // PIXI INITIALIZATION
  // ============================================================================

  useEffect(() => {
    console.log('app created', pixiLoaded);
    let start = performance.now();
    updateQueueRef.current.clear();
    let destroyListeners: () => void;
    if (!appRef.current) {
      console.log('new app');
      const pixiApp = new Application();
      pixiApp
        .init({
          backgroundColor: '#b0b0b0',
          resolution: Math.max(window.devicePixelRatio, 1),
        })
        .then(async () => {
          console.log('app init in ', performance.now() - start, 'ms');

          Assets.init({ manifest: manifest }).then(async () => {
            const bundleIds = manifest.bundles.map((bundle) => bundle.name);
            await Assets.loadBundle(bundleIds);
          });

          appRef.current = pixiApp;
          pixiRef.current?.appendChild(appRef.current.canvas);

          appRef.current.stage.addChild(containerRef.current);
          // Allow zIndex to affect render order
          appRef.current.stage.sortableChildren = true;
          let renderStart = performance.now();
          // renderMap();
          console.log('render map in ', performance.now() - renderStart, 'ms');
          const destroy = addListenerContainer();
          if (destroy) {
            destroyListeners = destroy;
          }

          // // Tạo debug text
          // createDebugText();

          // Tạo FPS text
          createFPSText();

          // Thiết lập FPS counter
          // setupFPSCounter();

          resizeCanvas();
          setPixiLoaded(true);

          console.log('App loaded in ', performance.now() - start, 'ms');
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('error init app', error);
          setIsLoading(false);
        });
    }
    window.addEventListener('resize', resizeCanvas);

    // Save ticker callback reference for cleanup
    const tickerCallback = (ticker: Ticker) => update(ticker.deltaMS);
    Ticker.shared.add(tickerCallback);

    return () => {
      Ticker.shared.remove(tickerCallback);
      // Cleanup tooltip khi component unmount
      console.log('__UN MOUNT MAP ___');
      window.removeEventListener('resize', resizeCanvas);
      hidePixiTooltip();

      // Cleanup textures
      areaTexturesRef.current.forEach((texture) => {
        if (texture) texture.destroy(true);
      });
      areaTexturesRef.current = [];
      areaSpritesRef.current = [];
      graphicsCellRef.current = [];

      if (appRef.current) {
        console.log('destroy app');
        appRef.current.destroy(true, true);
        appRef.current = null;
      }

      if (destroyListeners) {
        destroyListeners();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resizeCanvas = useCallback(() => {
    console.log('resizeCanvas');
    if (!appRef.current) return;

    const parentSize = document
      .querySelector('.map-container')
      ?.getBoundingClientRect();
    if (!parentSize) return;

    const windowWidth = parentSize.width;
    const windowHeight = parentSize.height;

    // Sử dụng props thay vì hardcode
    const DESIGN_WIDTH = width;
    const DESIGN_HEIGHT = height;

    // Tính toán kích thước canvas
    let logicWidth = Math.min(windowWidth, DESIGN_WIDTH);
    let logicHeight = Math.min(windowHeight, DESIGN_HEIGHT);

    // Cập nhật debug text nếu đã có
    if (debugTextRef.current) {
      updateDebugText(`Canvas: ${logicWidth}x${logicHeight}`);
    }

    appRef.current.renderer.resize(logicWidth, logicHeight);

    if (pixiRef.current) {
      pixiRef.current.style.width = `${logicWidth}px`;
      pixiRef.current.style.height = `${logicHeight}px`;
    }

    // // --- Step 5: Reset canvas styles
    const canvas = appRef.current.canvas;
    if (canvas) {
      canvas.style.width = `${logicWidth}px`;
      canvas.style.height = `${logicHeight}px`;
    }

    if (containerRef.current) {
      containerRef.current.scale?.set(0.5);
      // Lưu trạng thái ban đầu cho reset button (chỉ lưu lần đầu)
      if (!initialMapStateRef.current) {
        initialMapStateRef.current = {
          x: containerRef.current.x || 0,
          y: containerRef.current.y || 0,
          scale: 0.5,
        };
      }
    }

    // Cập nhật vị trí FPS text sau khi resize
    if (fpsTextRef.current) {
      updateFPSTextPosition();
    }
  }, [width, height]);

  // Hàm tạo debug text
  const createDebugText = () => {
    if (!appRef.current?.stage) return;

    // Tạo debug text
    const debugText = new Text('Debug: Ready', {
      fontSize: 14,
      fill: '#ff0000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
    });

    // Đặt vị trí ở góc trên bên trái
    debugText.position.set(10, 10);
    debugText.zIndex = 1002; // Cao hơn tooltip và UI

    // Lưu reference
    debugTextRef.current = debugText;

    // Thêm vào stage
    appRef.current.stage.addChild(debugText);
    console.log('Debug text created and added to stage');
  };

  // Hàm tạo FPS text
  const createFPSText = () => {
    if (!appRef.current?.stage) return;
    if (!fpsTextRef.current) return;
    fpsTextRef.current.position.set(10, 10);
    fpsTextRef.current.zIndex = 1003; // Cao hơn debug text
    appRef.current.stage.addChild(fpsTextRef.current);
    updateFPSTextPosition();
  };

  // Hàm cập nhật vị trí FPS text
  const updateFPSTextPosition = () => {
    if (!fpsTextRef.current || !appRef.current?.canvas) return;

    const canvasRect = appRef.current.canvas.getBoundingClientRect();
    const rightMargin = 20;
    const topMargin = 10;

    fpsTextRef.current.position.set(
      canvasRect.width - fpsTextRef.current.width - rightMargin,
      topMargin
    );
  };

  // Hàm cập nhật FPS text
  const updateFPSText = (fps: number) => {
    if (fpsTextRef.current && fpsTextRef.current.style) {
      fpsTextRef.current.text = `FPS: ${Math.round(fps)}`;
      if (fps < 30) {
        fpsTextRef.current.style.fill = '#ff0000';
      } else {
        fpsTextRef.current.style.fill = '#00ff00';
      }
    }
  };

  // Hàm cập nhật debug text
  const updateDebugText = (text: string) => {
    if (debugTextRef.current) {
      debugTextRef.current.text = `Debug: ${text}`;
    }
  };

  const drawArea = (
    data: Cell[],
    g: Graphics,
    sizeCell: number,
    _x: number,
    _y: number
  ) => {
    const sizeOfACell = sizeCell / cellSideOfSquare;
    data.forEach((item: Cell, index: number) => {
      for (let row = 0; row < cellSideOfSquare; row++) {
        for (let col = 0; col < cellSideOfSquare; col++) {
          const c = item.c?.[row * cellSideOfSquare + col] || 0;
          // getHexMapping(item.c?.[row * cellSideOfSquare + col] || 0) ||
          // '000000';

          const x = col * sizeOfACell + item.x * sizeCell - _x;
          const y = row * sizeOfACell + item.y * sizeCell - _y;
          g.rect(x, y, sizeOfACell, sizeOfACell).fill({ color: c });
        }
      }
    });
  };

  // ============================================================================
  // RENDERING FUNCTIONS
  // ============================================================================

  /**
   * Render a specific area by index
   * @param areaIndex - Index of area to render (0-4)
   * @param areaData - Optional: specific data to render (for real-time updates)
   */
  const renderArea = (areaIndex: number, areaData?: MapData[]) => {
    if (!appRef.current || !containerRef.current) return;

    // Use passed data or fall back to prop data
    const currentData = areaData || data;
    if (areaIndex < 0 || areaIndex >= currentData.length) return;

    const area = currentData[areaIndex];

    // Destroy old texture if exists (force fresh render)
    const oldTexture = areaTexturesRef.current[areaIndex];
    if (oldTexture) {
      oldTexture.destroy(true);
    }

    // Create NEW texture
    const texture = RenderTexture.create({
      width: area.width * cellSideOfSquare,
      height: area.height * cellSideOfSquare,
      resolution: 1,
    });
    areaTexturesRef.current[areaIndex] = texture;

    // Get or create graphics
    let graphics = graphicsCellRef.current[areaIndex];
    if (!graphics) {
      graphics = new Graphics();
      graphicsCellRef.current[areaIndex] = graphics;
    } else {
      // Clear previous graphics
      graphics.clear();
    }

    // DEBUG: Draw background color for each area
    // const debugColors = [
    //   "#ff0000",
    //   "#00ff00",
    //   "#ffdba1",
    //   "#ffff00",
    //   "#ff00ff",
    //   "#19f772",
    //   "#75f1ff",
    //   "#ffce52",
    //   "#6fa314",
    //   "#6e7ad4",
    //   "#ffc7ef",
    //   "#ff0000",
    //   "#00ff00",
    //   "#ffdba1",
    //   "#ffff00",
    //   "#ff00ff",
    //   "#19f772",
    //   "#75f1ff",
    //   "#ffce52",
    //   "#6fa314",
    //   "#6e7ad4",
    //   "#ffc7ef",
    //   "#ff0000",
    //   "#00ff00",
    // ];
    // graphics.rect(0, 0, area.width * cellSideOfSquare, area.height * cellSideOfSquare);
    // graphics.fill(debugColors[areaIndex] || 0x1e28e3);

    // Draw cells into graphics
    const spriteX = area.x * cellSideOfSquare;
    const spriteY = area.y * cellSideOfSquare;
    drawArea(area.data, graphics, cellSideOfSquare, spriteX, spriteY);

    // Render graphics to NEW texture
    appRef.current.renderer.render(graphics, { renderTexture: texture });

    // Destroy old sprite and create new one with new texture
    let sprite = areaSpritesRef.current[areaIndex];
    if (sprite) {
      containerRef.current.removeChild(sprite);
      sprite.destroy(true); // Destroy completely (but texture already destroyed separately)
    }

    // Create new sprite with NEW texture
    sprite = new Sprite(texture);
    sprite.position.set(spriteX, spriteY);
    areaSpritesRef.current[areaIndex] = sprite;
    containerRef.current.addChild(sprite);

    // Force PIXI to render the stage immediately
    if (appRef.current) {
      appRef.current.render();
    }
  };

  /**
   * Initial render of all areas
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderMap = () => {
    for (let i = 0; i < data.length; i++) {
      renderArea(i);
    }
  };

  const addListenerContainer = () => {
    let isTouch = false;
    let isZooming = false;
    let touchesSaver: { [id: number]: any } = {};

    const canvas = appRef.current?.canvas;
    if (!canvas) return null;
    // Prevent browser gestures (e.g., double-tap zoom) on touch devices
    canvas.style.touchAction = 'none';

    const onWheel = (e: WheelEvent) => {
      if (isPixelEditorOpenRef.current) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      // Lấy vị trí chuột trên window
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      // Tính tỉ lệ zoom
      let newScale = 1;
      if (e.deltaY < 0) {
        // Zoom in
        newScale = ZOOM_FACTOR;
      } else {
        // Zoom out
        newScale = 1 / ZOOM_FACTOR;
      }

      // Gọi hàm zoom với điểm target
      zoomToPoint(mouseX, mouseY, newScale);
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });

    const onPointerDown = (e: any) => {
      // Kiểm tra xem click có nằm trong vùng toolbar không
      const currentToolbar = (appRef.current?.stage as any)?.currentToolbar;
      if (currentToolbar) {
        const canvasRect = canvas.getBoundingClientRect();
        const screenX = e.clientX;
        const screenY = e.clientY;

        // Chuyển đổi tọa độ toolbar sang screen coordinates
        const toolbarBounds = currentToolbar.getBounds();
        const globalPos = currentToolbar.toGlobal({ x: 0, y: 0 });

        const toolbarScreenX = globalPos.x + canvasRect.left;
        const toolbarScreenY = globalPos.y + canvasRect.top;

        // Kiểm tra xem click có nằm trong vùng toolbar không (với margin để an toàn)
        const margin = 5;
        if (
          screenX >= toolbarScreenX - margin &&
          screenX <= toolbarScreenX + toolbarBounds.width + margin &&
          screenY >= toolbarScreenY - margin &&
          screenY <= toolbarScreenY + toolbarBounds.height + margin
        ) {
          // Click vào toolbar, không xử lý
          isToolbarClickRef.current = true;
          return;
        }
      }

      if (isToolbarClickRef.current || isPixelEditorOpenRef.current) {
        return;
      }
      isTouch = false;
      e.preventDefault(); // Ngăn chặn zoom mặc định của browser
      touchesSaver[e.pointerId] = e;
      // Nếu có 2 ngón tay trở lên, không bắt đầu drag
      if (Object.keys(touchesSaver).length >= 2) {
        dragging = false;
        return;
      }
      // Lưu vị trí bắt đầu để so sánh sau
      dragStartPos = new Point(e.clientX, e.clientY);

      lastPos = new Point(e.clientX, e.clientY);
    };
    canvas.addEventListener('pointerdown', onPointerDown);

    const onTouchStart = (e: TouchEvent) => {
      if (isToolbarClickRef.current || isPixelEditorOpenRef.current) return;
      isTouch = true;
      e.preventDefault(); // Ngăn chặn zoom mặc          định của browser

      if (e.touches.length === 1) {
        const touch = e.touches[0];
        // Lưu vị trí bắt đầu để so sánh sau
        dragStartPos = new Point(touch.clientX, touch.clientY);
        lastPos = new Point(touch.clientX, touch.clientY);
      }
    };
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });

    const onTouchEnd = async (e: TouchEvent) => {
      if (isPixelEditorOpenRef.current) return;

      // Kiểm tra thời gian touch
      updateDebugText(`touchend: isDrag ${touchDragging} isZoom ${isZooming}`);

      // Chỉ hiển thị tooltip khi không phải drag và thời gian < 200ms (click nhanh)
      if (!touchDragging && !isZooming) {
        // Kiểm tra xem có phải click từ toolbar hay không
        if (isToolbarClickRef.current) {
          isToolbarClickRef.current = false; // Reset flag
          return; // Không xử lý click ô nếu click từ toolbar
        }

        // Đây là click thực sự, hiển thị tooltip
        const touch = e.changedTouches[0]; // Lấy touch cuối cùng
        const canvasRect = canvas.getBoundingClientRect();
        const localX = touch.clientX - canvasRect.left;
        const localY = touch.clientY - canvasRect.top;
        console.log('click xy', localX, localY);

        // Tính toán tọa độ thế giới với scale hiện tại
        if (!containerRef.current) return;
        const currentScale = containerRef.current?.scale.x;

        const worldX = (localX - containerRef.current.x) / currentScale;
        const worldY = (localY - containerRef.current.y) / currentScale;

        // Chuyển sang tọa độ logic của ô
        const logicX = Math.floor(worldX / cellSideOfSquare);
        const logicY = Math.floor(worldY / cellSideOfSquare);

        const data = dataService.getDetail(logicX, logicY);
        if (data) {
          if (lastSquareClickRef.current === data.i && data.l) {
            window.open(data.l, '_blank');
          }
          lastSquareClickRef.current = data.i || null;
          updateDebugText(`Touch square ${data.i}`);
          showPixiTooltip(data, touch.clientX, touch.clientY);
        } else {
          updateDebugText(`Touch outside `);
          lastSquareClickRef.current = null;
        }
      }

      // Reset touch dragging state
      touchDragging = false;
      if (e.touches.length === 0) {
        isZooming = false;
      }

      // Reset pinch state khi không còn touch
      if (e.touches.length === 0) {
        pinchState.current.initialDistance = 0;
        pinchState.current.initialScale = 1;
        pinchState.current.initialCenter = { x: 0, y: 0 };
      }
    };
    canvas.addEventListener('touchend', onTouchEnd, { passive: false });

    // Thêm touchmove event để hỗ trợ pinch zoom và drag trên iPhone
    const onTouchMove = (e: TouchEvent) => {
      if (isPixelEditorOpenRef.current) return;

      e.preventDefault(); // Ngăn chặn scroll mặc định
      updateDebugText('touchmove');

      // Xử lý pinch zoom khi có 2 ngón tay
      if (e.touches.length === 2) {
        isZooming = true;
        handleTouchPinchZoom(e);
        return;
      }

      // Xử lý drag khi có 1 ngón tay
      if (e.touches.length === 1) {
        // Kiểm tra xem có phải drag hay không
        const touch = e.touches[0];
        const distance = Math.sqrt(
          Math.pow(touch.clientX - dragStartPos.x, 2) +
            Math.pow(touch.clientY - dragStartPos.y, 2)
        );

        if (distance > dragThreshold) {
          if (isZooming) {
            isZooming = false;
            lastPos.x = touch.clientX;
            lastPos.y = touch.clientY;
          }
          touchDragging = true;
          // Xử lý di chuyển map
          const dx = touch.clientX - lastPos.x;
          const dy = touch.clientY - lastPos.y;
          if (!containerRef.current) return;
          containerRef.current.x += dx;
          containerRef.current.y += dy;
          lastPos.x = touch.clientX;
          lastPos.y = touch.clientY;

          // Ẩn tooltip khi drag
          hidePixiTooltip();
        }
      }
    };
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });

    const handlePointerUp = async (e: any) => {
      // Kiểm tra xem pointerId có trong danh sách toolbar clicks không
      if (
        e.pointerId !== undefined &&
        toolbarPointerIdsRef.current.has(e.pointerId)
      ) {
        // Đây là pointer đã click vào toolbar, bỏ qua
        toolbarPointerIdsRef.current.delete(e.pointerId);
        setTimeout(() => {
          isToolbarClickRef.current = false;
        }, 200);
        return;
      }

      // Reset toolbar click flag khi pointer up để đảm bảo map hoạt động lại
      const wasToolbarClick = isToolbarClickRef.current;

      // Nếu là toolbar click, kiểm tra xem click có nằm trong vùng toolbar không
      if (wasToolbarClick) {
        const currentToolbar = (appRef.current?.stage as any)?.currentToolbar;
        if (currentToolbar) {
          const canvasRect = canvas.getBoundingClientRect();
          const screenX = e.clientX;
          const screenY = e.clientY;

          // Chuyển đổi tọa độ toolbar sang screen coordinates
          const toolbarBounds = currentToolbar.getBounds();
          const globalPos = currentToolbar.toGlobal({ x: 0, y: 0 });

          const toolbarScreenX = globalPos.x + canvasRect.left;
          const toolbarScreenY = globalPos.y + canvasRect.top;

          // Kiểm tra xem click có nằm trong vùng toolbar không (với margin để an toàn)
          const margin = 5;
          if (
            screenX >= toolbarScreenX - margin &&
            screenX <= toolbarScreenX + toolbarBounds.width + margin &&
            screenY >= toolbarScreenY - margin &&
            screenY <= toolbarScreenY + toolbarBounds.height + margin
          ) {
            // Click vào toolbar, không xử lý click vào ô
            setTimeout(() => {
              isToolbarClickRef.current = false;
            }, 200);
            return;
          }
        }
        // Nếu không phải click vào toolbar (có thể flag bị set nhầm), reset flag
        isToolbarClickRef.current = false;
      }

      if (wasToolbarClick || isPixelEditorOpenRef.current) return;
      if (isTouch) return;

      updateDebugText(`pointerup mouse:${dragging} touch:${touchDragging}`);
      delete touchesSaver[e.pointerId];
      // Kiểm tra xem có phải click thực sự hay không
      if (!dragging) {
        // Đây là click thực sự, hiển thị tooltip
        const canvasRect = canvas.getBoundingClientRect();
        const localX = e.clientX - canvasRect.left;
        const localY = e.clientY - canvasRect.top;

        // Tính toán tọa độ thế giới với scale hiện tại
        if (!containerRef.current) return;
        const currentScale = containerRef.current?.scale.x;
        const worldX = (localX - containerRef.current?.x) / currentScale;
        const worldY = (localY - containerRef.current?.y) / currentScale;

        // Chuyển sang tọa độ logic của ô
        const logicX = Math.floor(worldX / cellSideOfSquare);
        const logicY = Math.floor(worldY / cellSideOfSquare);
        console.log('click xy', logicX, logicY);

        const data = dataService.getDetail(logicX, logicY);
        if (data) {
          if (lastSquareClickRef.current === data.i && data.l) {
            window.open(data.l, '_blank');
          }
          lastSquareClickRef.current = data.i || null;
          updateDebugText(`Click square ${data.i}`);
          showPixiTooltip(data, e.clientX, e.clientY);
        } else {
          updateDebugText(`Click outside `);
          lastSquareClickRef.current = null;
        }
      }

      dragging = false;
    };

    canvas.addEventListener('pointerup', handlePointerUp);

    const handlePointerUpOutside = (e: any) => {
      if (isTouch) return;
      delete touchesSaver[e.pointerId];
      dragging = false;

      // Reset toolbar click flag nếu có
      if (isToolbarClickRef.current) {
        isToolbarClickRef.current = false;
      }

      // Reset pinch state khi không còn touch
      if (Object.keys(touchesSaver).length === 0) {
        pinchState.current.initialDistance = 0;
        pinchState.current.initialScale = 1;
        pinchState.current.initialCenter = { x: 0, y: 0 };
      }
    };
    canvas.addEventListener('pointerupoutside', handlePointerUpOutside);

    const handleTouchCancel = (e: TouchEvent) => {
      touchDragging = false;
    };
    canvas.addEventListener('touchcancel', handleTouchCancel);

    const handlePointerMove = (e: any) => {
      if (isTouch || isPixelEditorOpenRef.current) return;
      // Cập nhật touch position
      if (touchesSaver[e.pointerId]) {
        touchesSaver[e.pointerId] = e;
      }

      // Xử lý pinch zoom khi có 2 ngón tay
      if (Object.keys(touchesSaver).length === 2) {
        handlePinchZoom(e, touchesSaver);
        return;
      }

      // Xử lý drag khi có 1 ngón tay
      if (Object.keys(touchesSaver).length === 1) {
        // Kiểm tra xem có phải drag hay không
        const distance = Math.sqrt(
          Math.pow(e.clientX - dragStartPos.x, 2) +
            Math.pow(e.clientY - dragStartPos.y, 2)
        );

        if (distance > dragThreshold) {
          if (!dragging) {
            dragging = true;
            updateDebugText('Mouse drag started');
          }

          // Xử lý di chuyển map
          const dx = e.clientX - lastPos.x;
          const dy = e.clientY - lastPos.y;
          if (!containerRef.current) return;
          containerRef.current.x += dx;
          containerRef.current.y += dy;
          lastPos.x = e.clientX;
          lastPos.y = e.clientY;

          // Ẩn tooltip khi drag
          hidePixiTooltip();
        }
      }
    };
    canvas.addEventListener('pointermove', handlePointerMove);

    const destroyListeners = () => {
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointerupoutside', handlePointerUpOutside);
      canvas.removeEventListener('touchcancel', handleTouchCancel);
      canvas.removeEventListener('pointermove', handlePointerMove);
    };
    return destroyListeners;
  };

  // Xử lý pinch zoom
  const handlePinchZoom = (e: any, touches: { [id: number]: any }) => {
    const touchIds = Object.keys(touches);
    if (touchIds.length !== 2) return;

    const touch1 = touches[parseInt(touchIds[0])];
    const touch2 = touches[parseInt(touchIds[1])];

    if (!touch1 || !touch2) return;

    // Tính khoảng cách giữa 2 ngón tay
    const currentDistance = Math.sqrt(
      Math.pow(touch1.clientX - touch2.clientX, 2) +
        Math.pow(touch1.clientY - touch2.clientY, 2)
    );

    // Lưu khoảng cách ban đầu nếu chưa có
    if (pinchState.current.initialDistance === 0) {
      pinchState.current.initialDistance = currentDistance;
      if (!containerRef.current) return;
      pinchState.current.initialScale = containerRef.current.scale.x;
      pinchState.current.initialCenter = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };
    }

    // Tính tỉ lệ zoom dựa trên thay đổi khoảng cách
    const scaleRatio = currentDistance / pinchState.current.initialDistance;
    const newScale = pinchState.current.initialScale * scaleRatio;

    // Áp dụng giới hạn min/max
    const clampedScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

    // Zoom vào điểm trung tâm giữa 2 ngón tay (chuyển sang tọa độ local của canvas)
    if (pinchState.current.initialCenter && appRef.current?.canvas) {
      const rect = appRef.current.canvas.getBoundingClientRect();
      const localX = pinchState.current.initialCenter.x - rect.left;
      const localY = pinchState.current.initialCenter.y - rect.top;
      zoomToPoint(localX, localY, clampedScale / containerRef.current.scale.x);
    }
  };

  // Xử lý pinch zoom cho touch events (iPhone)
  const handleTouchPinchZoom = (e: TouchEvent) => {
    if (e.touches.length !== 2) return;

    const touch1 = e.touches[0];
    const touch2 = e.touches[1];

    // Tính khoảng cách giữa 2 ngón tay
    const currentDistance = Math.sqrt(
      Math.pow(touch1.clientX - touch2.clientX, 2) +
        Math.pow(touch1.clientY - touch2.clientY, 2)
    );

    // Lưu khoảng cách ban đầu nếu chưa có
    if (pinchState.current.initialDistance === 0) {
      pinchState.current.initialDistance = currentDistance;
      if (!containerRef.current) return;
      pinchState.current.initialScale = containerRef.current.scale.x;
      pinchState.current.initialCenter = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };
    }

    // Tính tỉ lệ zoom dựa trên thay đổi khoảng cách
    const scaleRatio = currentDistance / pinchState.current.initialDistance;
    const newScale = pinchState.current.initialScale * scaleRatio;

    // Áp dụng giới hạn min/max
    const clampedScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

    // Zoom vào điểm trung tâm giữa 2 ngón tay (chuyển sang tọa độ local của canvas)
    if (pinchState.current.initialCenter && appRef.current?.canvas) {
      const rect = appRef.current.canvas.getBoundingClientRect();
      const localX = pinchState.current.initialCenter.x - rect.left;
      const localY = pinchState.current.initialCenter.y - rect.top;
      zoomToPoint(localX, localY, clampedScale / containerRef.current?.scale.x);
    }
  };

  // Hàm zoom vào điểm target cụ thể
  const zoomToPoint = (
    targetX: number,
    targetY: number,
    zoomFactor: number
  ) => {
    if (!containerRef.current) return;

    // Ẩn tooltip khi zoom
    hidePixiTooltip();

    updateDebugText(`Zoom: ${zoomFactor > 1 ? 'In' : 'Out'}`);

    // Lấy scale hiện tại của container
    const currentScale = containerRef.current?.scale.x;

    // Tính scale mới với giới hạn min/max
    let newScale = currentScale * zoomFactor;
    newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

    // Nếu scale không thay đổi thì không làm gì
    if (newScale === currentScale) return;

    // Tính toán vị trí thế giới trước khi zoom
    const worldPos = {
      x: (targetX - containerRef.current?.x) / currentScale,
      y: (targetY - containerRef.current?.y) / currentScale,
    };
    // Cập nhật scale mới
    containerRef.current?.scale.set(newScale);

    // Tính lại vị trí container để giữ điểm target cố định
    containerRef.current.x = targetX - worldPos.x * newScale;
    containerRef.current.y = targetY - worldPos.y * newScale;

    updateDebugText(`Scale: ${containerRef.current?.scale.x.toFixed(2)}`);
  };

  // Hàm reset map về vị trí ban đầu
  const resetMapPosition = () => {
    if (!containerRef.current || !initialMapStateRef.current) return;

    // Ẩn tooltip khi reset
    hidePixiTooltip();

    // Reset về vị trí và scale ban đầu
    containerRef.current.x = initialMapStateRef.current.x;
    containerRef.current.y = initialMapStateRef.current.y;
    containerRef.current.scale.set(initialMapStateRef.current.scale);

    updateDebugText('Map reset to initial position');
  };

  // Hàm hiển thị tooltip trong PIXI
  const showPixiTooltip = (data: Cell, x: number, y: number) => {
    if (!appRef.current?.stage) return;

    // Xóa tooltip cũ nếu có
    hidePixiTooltip();

    // Tạo tooltip container
    const tooltipContainer = new Container();
    tooltipContainer.zIndex = 1001; // Cao hơn UI

    // Background của tooltip
    const tooltipBg = new Graphics();
    const padding = 10;
    const textWidth = Math.min(250, appRef.current.screen.width - padding * 2);

    const text = new Text(
      data.t || (data.o ? `Sở hữu bởi ${data.o}` : `Ô ${data.i} đang trống`),
      {
        fontSize: 16,
        fill: '#ff0000',
        fontFamily: 'Arial',
        fontWeight: 'bold',
        wordWrap: true,
        wordWrapWidth: textWidth,
      }
    );

    text.position.set(padding, padding);

    tooltipBg
      .rect(0, 0, text.width + padding * 2, text.height + padding * 2)
      .fill('#ffffff');

    // Thêm border
    tooltipBg.stroke({ color: '#000000', width: 2 });

    tooltipContainer.addChild(tooltipBg);
    tooltipContainer.addChild(text);

    // Tính toán vị trí tooltip chính xác tại ô được click
    const canvasRect = appRef.current.canvas?.getBoundingClientRect();
    if (canvasRect) {
      // Chuyển từ tọa độ logic sang tọa độ thế giới
      const worldX = data.x * cellSideOfSquare;
      const worldY = data.y * cellSideOfSquare;

      // Áp dụng scale và position của container để có tọa độ canvas
      const currentScale = containerRef.current?.scale.x;
      const canvasX = worldX * currentScale + containerRef.current?.x;
      const canvasY = worldY * currentScale + containerRef.current?.y;

      // Đặt tooltip tại vị trí ô (trên ô một chút)
      let tooltipX = canvasX + 1;
      let tooltipY = canvasY - tooltipContainer.height;

      // Kiểm tra và điều chỉnh vị trí X để tooltip không bị overflow ra ngoài màn hình
      const tooltipWidth = tooltipContainer.width;
      const screenWidth = appRef.current.screen.width;

      // Nếu tooltip vượt quá bên phải màn hình, di chuyển sang trái
      if (tooltipX + tooltipWidth > screenWidth) {
        tooltipX = screenWidth - tooltipWidth - padding;
      }

      // Nếu tooltip vượt quá bên trái màn hình, di chuyển sang phải
      if (tooltipX < 0) {
        tooltipX = padding;
      }

      tooltipContainer.position.set(tooltipX, tooltipY);
    } else {
      // Fallback: đặt tooltip tại vị trí chuột
      let tooltipX = x;
      let tooltipY = y - tooltipContainer.height;

      // Kiểm tra và điều chỉnh vị trí X cho fallback case
      const tooltipWidth = tooltipContainer.width;
      const screenWidth = appRef.current.screen.width;

      if (tooltipX + tooltipWidth > screenWidth) {
        tooltipX = screenWidth - tooltipWidth - padding;
      }

      if (tooltipX < 0) {
        tooltipX = padding;
      }

      tooltipContainer.position.set(tooltipX, tooltipY);
    }

    // Tạo thanh công cụ
    createToolbar(data);

    // Lưu reference để có thể xóa sau
    (appRef.current.stage as any).currentTooltip = tooltipContainer;
    (appRef.current.stage as any).currentTooltipData = data;
    (appRef.current.stage as any).currentTooltipPosition = { x, y };
    appRef.current.stage.addChild(tooltipContainer);
  };

  // Hàm kiểm tra có thể vẽ hay không
  const canDraw = (data: Cell): boolean => {
    if (!data.timestamp) return true; // Chưa có timestamp thì có thể vẽ
    const now = Date.now();
    const timeSinceLastDraw = (now - data.timestamp) / 1000; // Convert to seconds
    return timeSinceLastDraw >= 30; // Phải hơn 30 giây
  };

  // Hàm tính thời gian còn lại
  const getTimeRemaining = (data: Cell): number => {
    if (!data.timestamp) return 0;
    const now = Date.now();
    const timeSinceLastDraw = (now - data.timestamp) / 1000;
    return Math.max(0, 30 - timeSinceLastDraw);
  };

  // Hàm xử lý click icon link (reserved for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLinkClick = (e: any) => {
    e.stopPropagation(); // Ngăn chặn event bubble lên canvas
    isToolbarClickRef.current = true; // Đánh dấu click từ toolbar
    console.log('clicked link');
  };

  // Hàm xử lý click icon edit
  const handleEditClick = (e: any, cellData: Cell) => {
    e.stopPropagation(); // Ngăn chặn event bubble lên canvas
    isToolbarClickRef.current = true; // Đánh dấu click từ toolbar

    // Kiểm tra đăng nhập trước khi cho phép edit - CHECK PROP TRỰC TIẾP
    console.log('[Map] handleEditClick - isLoggedIn:', isLoggedIn, 'uid:', uid);
    if (!isLoggedIn) {
      console.log('[Map] Login required for edit, calling onRequireAuth');
      if (onRequireAuth) {
        onRequireAuth(); // Show login popup
      }
      return;
    }

    console.log('[Map] User is logged in, showing pixel editor');
    // Show pixel editor popup
    showPixelEditor(cellData);
  };

  // Hàm xử lý click icon buy
  const handleBuyClick = (e: any, cellData: Cell) => {
    e.stopPropagation(); // Ngăn chặn event bubble lên canvas
    isToolbarClickRef.current = true; // Đánh dấu click từ toolbar

    // Kiểm tra đăng nhập trước khi cho phép buy - CHECK PROP TRỰC TIẾP
    console.log('[Map] handleBuyClick - isLoggedIn:', isLoggedIn, 'uid:', uid);
    if (!isLoggedIn) {
      console.log('[Map] Login required for buy, calling onRequireAuth');
      if (onRequireAuth) {
        onRequireAuth(); // Show login popup
      }
      return;
    }

    console.log('[Map] User is logged in, adding to cart');
    // Import cart service dynamically to avoid circular dependency
    import('../../services/cart.service').then(({ cartService }) => {
      const success = cartService.addToCart(cellData);
      if (success) {
        console.log(`[Map] Cell ${cellData.i} added to cart`);
        // Show notification (optional)
        window.dispatchEvent(
          new CustomEvent('show-toast', {
            detail: {
              message: `Đã thêm ô ${cellData.i} vào giỏ hàng`,
              type: 'success',
            },
          })
        );
      } else {
        console.log(`[Map] Cell ${cellData.i} already in cart`);
        window.dispatchEvent(
          new CustomEvent('show-toast', {
            detail: {
              message: `Ô ${cellData.i} đã có trong giỏ hàng`,
              type: 'info',
            },
          })
        );
      }
    });
  };

  // Hàm tạo countdown container với timer tự động cập nhật
  const createCountdownContainer = (
    cellData: Cell,
    buttonHeight: number,
    padding: number
  ): Container => {
    const countdownText = new Text(`${Math.ceil(getTimeRemaining(cellData))}s`, {
      fontSize: 14,
      fill: '#ff0000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
    });
    const countdownBg = new Graphics();
    countdownBg.rect(0, 0, 60, buttonHeight).fill('#cccccc');
    countdownText.position.set(8, 8);

    const countdownContainer = new Container();
    countdownContainer.addChild(countdownBg);
    countdownContainer.addChild(countdownText);

    // Lưu reference để có thể cập nhật
    (countdownContainer as any).countdownText = countdownText;
    (countdownContainer as any).cellData = cellData;

    // Tạo interval để cập nhật countdown mỗi giây
    const updateCountdown = () => {
      // Lấy data mới nhất từ dataService bằng tọa độ của cell
      const latestCellData = dataService.getDetail(cellData.x, cellData.y);
      const currentCellData = latestCellData || (appRef.current?.stage as any)?.currentTooltipData || cellData;
      const timeRemaining = getTimeRemaining(currentCellData);
      const seconds = Math.max(0, Math.ceil(timeRemaining));
      
      // Cập nhật text countdown
      if (countdownText && countdownText.text !== undefined) {
        countdownText.text = `${seconds}s`;
      }

      // Nếu hết thời gian, refresh toolbar để hiển thị nút "Vẽ" lại
      if (timeRemaining <= 0) {
        const countdownInterval = (appRef.current?.stage as any)?.countdownInterval;
        if (countdownInterval) {
          clearInterval(countdownInterval);
          (appRef.current!.stage as any).countdownInterval = null;
        }

        // Lấy data mới nhất và refresh toolbar chỉ khi thực sự có thể vẽ
        const refreshedCellData = dataService.getDetail(cellData.x, cellData.y);
        if (refreshedCellData && appRef.current?.stage) {
          // Kiểm tra lại xem có thể vẽ chưa
          if (canDraw(refreshedCellData)) {
            // Cập nhật currentTooltipData với data mới
            (appRef.current.stage as any).currentTooltipData = refreshedCellData;
            // Refresh toolbar với data mới - sẽ hiển thị nút "Vẽ"
            createToolbar(refreshedCellData);
          } else {
            // Nếu vẫn chưa thể vẽ, cập nhật text thành "0s"
            if (countdownText && countdownText.text !== undefined) {
              countdownText.text = '0s';
            }
          }
        }
      }
    };

    // Cleanup interval cũ nếu có
    const oldInterval = (appRef.current?.stage as any)?.countdownInterval;
    if (oldInterval) {
      clearInterval(oldInterval);
    }

    // Tạo interval mới - cập nhật mỗi giây
    const intervalId = setInterval(updateCountdown, 1000);
    (appRef.current!.stage as any).countdownInterval = intervalId;

    return countdownContainer;
  };

  // Hàm tạo thanh công cụ
  const createToolbar = (data: Cell) => {
    if (!appRef.current?.stage) return;

    // Xóa toolbar cũ nếu có
    hideToolbar();

    // Tạo toolbar container
    const toolbarContainer = new Container();
    toolbarContainer.zIndex = 1000; // Thấp hơn tooltip một chút

    // Background của toolbar
    const toolbarBg = new Graphics();
    const padding = 10;
    const iconSize = 24;
    const iconSpacing = 8;
    const buttonHeight = 32;

    // // Text "Ô 123"
    // const cellText = new Text(`Ô ${data.i || 'Unknown'}`, {
    //   fontSize: 16,
    //   fill: '#000000',
    //   fontFamily: 'Arial',
    //   fontWeight: 'bold',
    // });

    const elements: any[] = [];
    let currentX = padding;

    // Text "Ô x" để hiển thị số ô
    const cellText = new Text(`Ô ${data.i || 'Unknown'}`, {
      fontSize: 16,
      fill: '#000000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
    });
    cellText.position.set(currentX, padding + 8);
    elements.push(cellText);
    currentX += cellText.width + iconSpacing;

    // Kiểm tra xem có phải owner hay không
    console.log('data', data);
    const isOwner = data.o && data.o === uid;
    const hasOwner = data.o && data.o !== null;

    // Tính toán các element cần hiển thị
    if (!hasOwner) {
      // Ô chưa có chủ sở hữu - hiển thị 2 nút "Vẽ" và "Mua"
      const canDrawCell = canDraw(data);

      // Icon "Vẽ" (edit)
      if (canDrawCell) {
        const drawContainer = new Container();
        const drawButtonBg = new Graphics();
        const drawButtonSize = 40;
        drawButtonBg.rect(0, 0, drawButtonSize, drawButtonSize).fill('#4CAF50');
        drawButtonBg.stroke({ color: '#2f7c35', width: 2 });

        const editTexture = Texture.from('edit');
        const editSprite = Sprite.from(editTexture);
        editSprite.width = 24;
        editSprite.height = 24;
        editSprite.position.set(
          (drawButtonSize - editSprite.width) / 2,
          (drawButtonSize - editSprite.height) / 2
        );

        drawContainer.addChild(drawButtonBg);
        drawContainer.addChild(editSprite);
        drawContainer.eventMode = 'static';
        drawContainer.cursor = 'pointer';
        // Set hitArea cho button để capture clicks
        drawContainer.hitArea = new Rectangle(
          0,
          0,
          drawButtonSize,
          drawButtonSize
        );
        drawContainer.on('pointerdown', (e) => {
          e.stopPropagation(); // Ngăn chặn event bubble
          e.stopImmediatePropagation?.(); // Stop all propagation
          // Lưu pointerId để bỏ qua events tiếp theo
          if (e.pointerId !== undefined) {
            toolbarPointerIdsRef.current.add(e.pointerId);
          }
          handleEditClick(e, data);
        });
        drawContainer.on('pointerup', (e) => {
          e.stopPropagation(); // Ngăn chặn event bubble
          e.stopImmediatePropagation?.(); // Stop all propagation
        });
        drawContainer.on('pointerupoutside', (e) => {
          e.stopPropagation(); // Ngăn chặn event bubble
          e.stopImmediatePropagation?.(); // Stop all propagation
        });
        drawContainer.position.set(currentX, padding - 4);
        elements.push(drawContainer);
        currentX += drawButtonSize + 10;
      } else {
        // Hiển thị countdown với timer tự động cập nhật
        const countdownContainer = createCountdownContainer(data, buttonHeight, padding);
        countdownContainer.position.set(currentX, padding);
        elements.push(countdownContainer);
        currentX += 70;
      }

      // Icon "Mua" (cart) - chỉ hiển thị nếu ô chưa được checkout
      const isCellCheckedOut =
        data.i !== undefined && checkoutStateService.isCellCheckedOut(data.i);

      if (!isCellCheckedOut) {
        const buyContainer = new Container();
        const buyButtonBg = new Graphics();
        const buyButtonSize = 40;
        buyButtonBg.rect(0, 0, buyButtonSize, buyButtonSize).fill('#2196F3');
        buyButtonBg.stroke({ color: '#1a5fa7', width: 2 });

        const cartTexture = Texture.from('cart2');
        const cartSprite = Sprite.from(cartTexture);
        cartSprite.width = 28;
        cartSprite.height = 28;
        cartSprite.position.set(
          (buyButtonSize - cartSprite.width) / 2,
          (buyButtonSize - cartSprite.height) / 2
        );

        buyContainer.addChild(buyButtonBg);
        buyContainer.addChild(cartSprite);
        buyContainer.eventMode = 'static';
        buyContainer.cursor = 'pointer';
        // Set hitArea cho button để capture clicks
        buyContainer.hitArea = new Rectangle(
          0,
          0,
          buyButtonSize,
          buyButtonSize
        );
        buyContainer.on('pointerdown', (e) => {
          e.stopPropagation(); // Ngăn chặn event bubble
          e.stopImmediatePropagation?.(); // Stop all propagation
          // Lưu pointerId để bỏ qua events tiếp theo
          if (e.pointerId !== undefined) {
            toolbarPointerIdsRef.current.add(e.pointerId);
          }
          handleBuyClick(e, data);
        });
        buyContainer.on('pointerup', (e) => {
          e.stopPropagation(); // Ngăn chặn event bubble
          e.stopImmediatePropagation?.(); // Stop all propagation
          // Xóa pointerId sau khi đã xử lý xong
          if (e.pointerId !== undefined) {
            toolbarPointerIdsRef.current.delete(e.pointerId);
          }
        });
        buyContainer.on('pointerupoutside', (e) => {
          e.stopPropagation(); // Ngăn chặn event bubble
          e.stopImmediatePropagation?.(); // Stop all propagation
          // Xóa pointerId sau khi đã xử lý xong
          if (e.pointerId !== undefined) {
            toolbarPointerIdsRef.current.delete(e.pointerId);
          }
        });
        buyContainer.position.set(currentX, padding - 4);
        elements.push(buyContainer);
        currentX += buyButtonSize + 10;
      }
    } else {
      // Ô đã có chủ sở hữu
      if (!uid || !isOwner) {
        // User anonymous hoặc không phải chủ - hiển thị text "Sở hữu bởi..."
        const ownerText = new Text(`Sở hữu bởi ${data.o}`, {
          fontSize: 16,
          fill: '#000000',
          fontFamily: 'Arial',
          fontWeight: 'bold',
        });
        ownerText.position.set(currentX, padding + 8);
        elements.push(ownerText);
        currentX += ownerText.width + iconSpacing;
      } else {
        // Là chủ ô - hiển thị nút "Vẽ" (có countdown)
        const canDrawCell = canDraw(data);

        if (canDrawCell) {
          const drawContainer = new Container();
          const drawButtonBg = new Graphics();
          const drawButtonSize = 40;
          drawButtonBg
            .rect(0, 0, drawButtonSize, drawButtonSize)
            .fill('#4CAF50');
          drawButtonBg.stroke({ color: '#2f7c35', width: 2 });

          const editTexture = Texture.from('edit');
          const editSprite = Sprite.from(editTexture);
          editSprite.width = 24;
          editSprite.height = 24;
          editSprite.position.set(
            (drawButtonSize - editSprite.width) / 2,
            (drawButtonSize - editSprite.height) / 2
          );

          drawContainer.addChild(drawButtonBg);
          drawContainer.addChild(editSprite);
          drawContainer.eventMode = 'static';
          drawContainer.cursor = 'pointer';
          // Set hitArea cho button để capture clicks
          drawContainer.hitArea = new Rectangle(
            0,
            0,
            drawButtonSize,
            drawButtonSize
          );
          drawContainer.on('pointerdown', (e) => {
            e.stopPropagation(); // Ngăn chặn event bubble
            e.stopImmediatePropagation?.(); // Stop all propagation
            // Lưu pointerId để bỏ qua events tiếp theo
            if (e.pointerId !== undefined) {
              toolbarPointerIdsRef.current.add(e.pointerId);
            }
            handleEditClick(e, data);
          });
          drawContainer.on('pointerup', (e) => {
            e.stopPropagation(); // Ngăn chặn event bubble
            e.stopImmediatePropagation?.(); // Stop all propagation
            // Xóa pointerId sau khi đã xử lý xong
            if (e.pointerId !== undefined) {
              toolbarPointerIdsRef.current.delete(e.pointerId);
            }
          });
          drawContainer.on('pointerupoutside', (e) => {
            e.stopPropagation(); // Ngăn chặn event bubble
            e.stopImmediatePropagation?.(); // Stop all propagation
            // Xóa pointerId sau khi đã xử lý xong
            if (e.pointerId !== undefined) {
              toolbarPointerIdsRef.current.delete(e.pointerId);
            }
          });
          drawContainer.position.set(currentX, padding - 4);
          elements.push(drawContainer);
          currentX += drawButtonSize + 10;
        } else {
          // Hiển thị countdown với timer tự động cập nhật
          const countdownContainer = createCountdownContainer(data, buttonHeight, padding);
          countdownContainer.position.set(currentX, padding);
          elements.push(countdownContainer);
          currentX += 70;
        }
      }
    }

    // Tính toán kích thước toolbar
    const toolbarHeight = Math.max(buttonHeight, iconSize) + padding * 2;

    // Tính chiều rộng dựa trên elements
    let maxX = 0;
    elements.forEach((el) => {
      if (el.x + el.width > maxX) {
        maxX = el.x + el.width;
      }
    });
    const toolbarWidth = Math.max(maxX + padding, 200);

    // Vẽ background
    toolbarBg.rect(0, 0, toolbarWidth, toolbarHeight).fill('#ffffff');

    // Thêm border
    toolbarBg.stroke({ color: '#000000', width: 2 });

    // Thêm các element vào container
    toolbarContainer.addChild(toolbarBg);
    // toolbarContainer.addChild(cellText);
    elements.forEach((el) => toolbarContainer.addChild(el));

    // Đặt vị trí toolbar ở phía trên màn hình, căn giữa theo width của canvas
    const toolbarX = (appRef.current.screen.width - toolbarWidth) / 2;
    toolbarContainer.position.set(toolbarX, 10);

    // Ngăn chặn event bubble từ toolbar container
    toolbarContainer.eventMode = 'static';

    // Set hitArea cho toolbar container để capture tất cả clicks trong vùng đó
    // Phải tạo sau khi đã tính toán toolbarWidth và toolbarHeight
    toolbarContainer.hitArea = new Rectangle(0, 0, toolbarWidth, toolbarHeight);

    // Đảm bảo toolbar container có zIndex cao để capture events trước
    toolbarContainer.zIndex = 1000;

    toolbarContainer.on('pointerdown', (e: any) => {
      e.stopPropagation();
      e.stopImmediatePropagation?.(); // Stop all propagation if available
      isToolbarClickRef.current = true; // Đánh dấu click từ toolbar
      // Lưu pointerId để bỏ qua events tiếp theo
      if (e.pointerId !== undefined) {
        toolbarPointerIdsRef.current.add(e.pointerId);
      }
    });
    // Reset flag khi pointer up trên toolbar để đảm bảo map hoạt động lại
    toolbarContainer.on('pointerup', (e: any) => {
      e.stopPropagation();
      e.stopImmediatePropagation?.(); // Stop all propagation if available
      // Reset flag sau một chút để đảm bảo handlePointerUp của canvas không xử lý click
      setTimeout(() => {
        isToolbarClickRef.current = false;
      }, 200);
    });
    toolbarContainer.on('pointerupoutside', (e: any) => {
      e.stopPropagation();
      e.stopImmediatePropagation?.(); // Stop all propagation if available
      // Reset flag nếu pointer up bên ngoài toolbar
      setTimeout(() => {
        isToolbarClickRef.current = false;
      }, 200);
    });

    // Lưu reference để có thể xóa sau
    (appRef.current.stage as any).currentToolbar = toolbarContainer;
    appRef.current.stage.addChild(toolbarContainer);
  };

  // Hàm ẩn toolbar
  const hideToolbar = () => {
    if (!appRef.current?.stage) return;

    // Cleanup countdown interval nếu có
    const countdownInterval = (appRef.current.stage as any).countdownInterval;
    if (countdownInterval) {
      clearInterval(countdownInterval);
      (appRef.current.stage as any).countdownInterval = null;
    }

    const currentToolbar = (appRef.current.stage as any).currentToolbar;
    if (currentToolbar) {
      appRef.current.stage.removeChild(currentToolbar);
      (appRef.current.stage as any).currentToolbar = null;
    }
  };

  // Hàm ẩn tooltip trong PIXI
  const hidePixiTooltip = () => {
    if (!appRef.current?.stage) return;

    const currentTooltip = (appRef.current.stage as any).currentTooltip;
    if (currentTooltip) {
      appRef.current.stage.removeChild(currentTooltip);
      (appRef.current.stage as any).currentTooltip = null;
      (appRef.current.stage as any).currentTooltipData = null;
      (appRef.current.stage as any).currentTooltipPosition = null;
    }

    // Ẩn toolbar khi ẩn tooltip
    hideToolbar();

    // Reset flag toolbar click
    isToolbarClickRef.current = false;
  };

  // Hàm hiển thị pixel editor popup
  const showPixelEditor = (cellData: Cell) => {
    if (!appRef.current) return;

    // Set flag để chặn tất cả interaction với map
    isPixelEditorOpenRef.current = true;

    // Ẩn tooltip và toolbar
    hidePixiTooltip();

    // Get neighbor cells
    const neighborCells = dataService.getNeighborCells(cellData);

    // Tạo pixel editor instance
    pixelEditorRef.current = new PixelEditor({
      app: appRef.current,
      cellData,
      neighborCells,
      onClose: () => {
        hidePixelEditor();
      },
      onSave: (updatedData: UpdateCellRequest) => {
        console.log('Updated data:', updatedData);
        // Send data to server via socket
        socketService.updateCell(updatedData);
        hidePixelEditor();
      },
      onSaveDraft: (draftName: string, colors: string[]) => {
        // Open HTML popup for draft name input
        setShowSaveDraftPopup(true);
      },
    });

    pixelEditorRef.current.show();
  };

  // Hàm ẩn pixel editor
  const hidePixelEditor = () => {
    if (!pixelEditorRef.current) return;

    pixelEditorRef.current.hide();
    pixelEditorRef.current = null;
    isToolbarClickRef.current = false;

    // Reset flag để cho phép interaction với map trở lại
    isPixelEditorOpenRef.current = false;
  };

  // Cleanup timer khi component unmount

  let lastQueueCheckTime = 0;
  const update = (delta: number) => {
    if (fpsTextRef.current) {
      const currentTime = Date.now();
      if (currentTime - lastFPSUpdateRef.current >= FPS_UPDATE_INTERVAL) {
        const fps = Ticker.shared.FPS;
        updateFPSText(fps);
        lastFPSUpdateRef.current = currentTime;
      }
    }
    lastQueueCheckTime += delta;
    if (lastQueueCheckTime >= QUEUE_CHECK_INTERVAL) {
      lastQueueCheckTime = 0;
      checkQueueUpdateCell();
    }
  };

  /**
   * Process queued area updates
   * - Renders all areas in queue with latest data
   * - Clears queue after processing
   */
  const checkQueueUpdateCell = () => {
    if (updateQueueRef.current.size === 0) return;

    // Use stored snapshot (has latest changes)
    const dataToRender =
      prevDataRef.current.length > 0 ? prevDataRef.current : data;

    // Render all queued areas
    updateQueueRef.current.forEach((areaIndex) => {
      console.log('areaIndex', areaIndex);
      renderArea(areaIndex, dataToRender);
    });

    updateQueueRef.current.clear();
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  // Reset toolbar flag when popup closes
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger > 0) {
      isToolbarClickRef.current = false;
    }
  }, [resetTrigger]);

  const handleLoginClick = () => {
    setShowAuthPopup(true);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      authService.removeAccessToken();

      // Reconnect socket as anonymous user (without token)
      if (socketService.isConnected()) {
        try {
          console.log('[Map] Reconnecting socket after logout as anonymous...');
          await socketService.reconnect();
          console.log('[Map] Socket reconnected as anonymous user');
        } catch (error) {
          console.error(
            '[Map] Failed to reconnect socket after logout:',
            error
          );
        }
      }

      // Dispatch custom event to notify other components about logout
      window.dispatchEvent(
        new CustomEvent('auth-state-changed', {
          detail: { isLoggedIn: false, username: null, token: null },
        })
      );
      console.log('[Map] Dispatched auth-state-changed event (logout)');
    }
  };

  const handleLoginSuccess = async (token: string, user: string) => {
    setShowAuthPopup(false);

    // Reconnect socket with new token if socket is already connected
    if (socketService.isConnected()) {
      try {
        console.log('[Map] Reconnecting socket after login...');
        await socketService.reconnect();
        console.log(
          '[Map] Socket reconnected successfully with authentication'
        );
      } catch (error) {
        console.error('[Map] Failed to reconnect socket after login:', error);
      }
    }

    // Dispatch event to notify other components about login
    window.dispatchEvent(
      new CustomEvent('auth-state-changed', {
        detail: { isLoggedIn: true, username: user, token },
      })
    );
    console.log('[Map] Dispatched auth-state-changed event for login');
  };

  const handleSaveDraft = () => {
    if (!pixelEditorRef.current) return;

    const colors = pixelEditorRef.current.getColors();
    console.log('=== SAVE DRAFT ===');
    console.log('Draft Name:', draftName || 'Draft_' + Date.now());
    console.log('Grid Colors (100 cells):', colors);
    console.log('Total cells:', colors.length);
    console.log('==================');

    // TODO: Save draft to server/localStorage

    // Close popup and reset name
    setShowSaveDraftPopup(false);
    setDraftName('');
  };

  // Kiểm tra map đã load xong chưa (PIXI app đã khởi tạo và có data)
  const isMapReady = !isLoading && pixiLoaded && data.length > 0;

  return (
    <div className="map-container">
      {isMapReady && (
        <button
          className="map-menu-button"
          onClick={() => setShowBottomMenu(true)}
        >
          ...
        </button>
      )}

      {isMapReady && (
        <button
          className="map-reset-button"
          onClick={resetMapPosition}
          title="Về vị trí ban đầu"
        >
          ·
        </button>
      )}

      <BottomSheetMenu
        isOpen={showBottomMenu}
        onClose={() => setShowBottomMenu(false)}
        isLoggedIn={isLoggedIn}
        uid={uid}
        onLogout={handleLogout}
        onLoginClick={handleLoginClick}
        onShowHistory={() => {
          setShowBottomMenu(false);
          setShowHistoryPopup(true);
        }}
        onShowChangePassword={() => {
          setShowBottomMenu(false);
          setShowChangePasswordPopup(true);
        }}
      />

      <div ref={pixiRef}></div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="map-loading-overlay">
          <div className="map-loading-content">
            <div className="map-loading-spinner"></div>
            <div className="map-loading-text">Đang tải bản đồ...</div>
          </div>
        </div>
      )}

      {/* Save Draft Popup */}
      {showSaveDraftPopup && (
        <div className="save-draft-overlay">
          <div className="save-draft-popup">
            <h2 className="save-draft-title">Lưu bản nháp</h2>

            <div className="save-draft-form">
              <label htmlFor="draftName" className="save-draft-label">
                Tên bản nháp:
              </label>
              <input
                id="draftName"
                type="text"
                className="save-draft-input"
                placeholder="Nhập tên bản nháp..."
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveDraft();
                  } else if (e.key === 'Escape') {
                    setShowSaveDraftPopup(false);
                    setDraftName('');
                  }
                }}
              />
            </div>

            <div className="save-draft-buttons">
              <button
                className="save-draft-btn save-draft-btn-cancel"
                onClick={() => {
                  setShowSaveDraftPopup(false);
                  setDraftName('');
                }}
              >
                Hủy
              </button>
              <button
                className="save-draft-btn save-draft-btn-save"
                onClick={handleSaveDraft}
              >
                Lưu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Popup */}
      {showHistoryPopup && (
        <div
          className="map-history-popup-overlay"
          onClick={() => setShowHistoryPopup(false)}
        >
          <div
            className="map-history-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="map-history-popup-close"
              onClick={() => setShowHistoryPopup(false)}
            >
              ×
            </button>
            <History onClose={() => setShowHistoryPopup(false)} />
          </div>
        </div>
      )}

      {/* Auth Popup */}
      <AuthPopup
        isOpen={showAuthPopup}
        onClose={() => setShowAuthPopup(false)}
        onLoginSuccess={handleLoginSuccess}
        onShowToast={(message, type) => {
          console.log(`[Map] Toast: ${type} - ${message}`);
          // Dispatch toast event để hiển thị toast UI
          window.dispatchEvent(
            new CustomEvent('show-toast', {
              detail: { message, type },
            })
          );
        }}
        onForgotPasswordClick={() => {
          setShowAuthPopup(false);
          setShowForgotPasswordPopup(true);
        }}
      />

      {/* Forgot Password Popup */}
      <ForgotPasswordPopup
        isOpen={showForgotPasswordPopup}
        onClose={() => setShowForgotPasswordPopup(false)}
        onSendCode={async (username: string) => {
          const result = await authService.forgotPassword(username);
          if (!result.success) {
            console.error('[Map] Forgot password error:', result.message);
          }
          return result;
        }}
        onShowResetPopup={(username: string, email: string) => {
          setResetPasswordUsername(username);
          setResetPasswordEmail(email);
          setShowResetPasswordPopup(true);
        }}
        onShowToast={(message, type) => {
          console.log(`[Map] Toast: ${type} - ${message}`);
          // Dispatch toast event để hiển thị toast UI
          window.dispatchEvent(
            new CustomEvent('show-toast', {
              detail: { message, type },
            })
          );
        }}
        onBackToLogin={() => {
          setShowForgotPasswordPopup(false);
          setShowAuthPopup(true);
        }}
      />

      {/* Reset Password Popup */}
      <ResetPasswordPopup
        isOpen={showResetPasswordPopup}
        username={resetPasswordUsername}
        email={resetPasswordEmail}
        onClose={() => {
          setShowResetPasswordPopup(false);
          setResetPasswordUsername('');
          setResetPasswordEmail('');
        }}
        onResetPassword={async (username: string, pin: string, password: string) => {
          const result = await authService.resetPassword(username, pin, password);
          if (!result.success) {
            const e = new Error(result.message || 'Có lỗi xảy ra');
            (e as any).code = typeof result.code !== 'undefined' ? result.code : undefined;
            throw e;
          }
        }}
        onShowToast={(message, type) => {
          console.log(`[Map] Toast: ${type} - ${message}`);
          // Dispatch toast event để hiển thị toast UI
          window.dispatchEvent(
            new CustomEvent('show-toast', {
              detail: { message, type },
            })
          );
        }}
        onSuccessGoToLogin={() => {
          // Chuyển sang popup đăng nhập sau khi reset password thành công
          setShowResetPasswordPopup(false);
          setResetPasswordUsername('');
          setResetPasswordEmail('');
          setShowAuthPopup(true);
        }}
      />

      {/* Change Password Popup */}
      <ChangePasswordPopup
        isOpen={showChangePasswordPopup}
        onClose={() => setShowChangePasswordPopup(false)}
        onChangePassword={async (oldPassword: string, newPassword: string) => {
          const result = await authService.changePassword(oldPassword, newPassword);
          if (!result.success) {
            throw new Error(result.message || 'Có lỗi xảy ra khi đổi mật khẩu');
          }
        }}
        onShowToast={(message, type) => {
          console.log(`[Map] Toast: ${type} - ${message}`);
          // Dispatch toast event để hiển thị toast UI
          window.dispatchEvent(
            new CustomEvent('show-toast', {
              detail: { message, type },
            })
          );
        }}
      />
    </div>
  );
};

export default Map;
